/**
 * DoserneInstallBanner — first-time onboarding shown inside the
 * Doserne side menu.
 *
 * It advertises two actions:
 *   1. Install the Doserne Academy web app (PWA `beforeinstallprompt`).
 *   2. Enable push notifications for chat + daily-plan alerts.
 *
 * Rendered inside the DoserneShell drawer. Automatically hides once
 * both goals are achieved or the user explicitly taps "Senere" (which
 * is remembered in localStorage for 14 days).
 *
 * Also swaps the document's <link rel="manifest"> to
 * `/manifest-doserne.json` so the installed app scopes itself to
 * `/doserne` — the AllStillas marketplace manifest stays out of the
 * way.
 */
import React, { useEffect, useState } from "react";
import { Download, Bell, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useDosernePush } from "./useDosernePush";
import { useDeferredInstallPrompt } from "./useDoserneInstall";

const DISMISS_KEY = "DOSERNE_INSTALL_DISMISSED_AT";
const DISMISS_TTL_MS = 14 * 24 * 3600 * 1000;

export default function DoserneInstallBanner({ authHeader, onDone }) {
  // Manifest is swapped globally by DoserneShell — no need to do it here.
  const { canInstall, installed, install } = useDeferredInstallPrompt();
  const push = useDosernePush(authHeader);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
      return ts && (Date.now() - ts) < DISMISS_TTL_MS;
    } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  const doInstall = async () => {
    setBusy(true);
    const ok = await install();
    if (ok) toast.success("Doserne-appen er installert 🎉");
    setBusy(false);
  };

  const doEnablePush = async () => {
    setBusy(true);
    try {
      await push.enable();
      toast.success("Varsler er aktivert — du får beskjed om chat og nye planer");
    } catch (e) {
      toast.error(e?.message || "Kunne ikke aktivere varsler");
    }
    setBusy(false);
  };

  const doDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
    onDone?.();
  };

  // Hide the banner if:
  //   • The user tapped "Senere" recently, OR
  //   • Both goals already achieved (app installed AND push subscribed),
  //     so we don't nag them on every menu open.
  const pushReady = push.subscribed || push.permission === "denied" || !push.supported;
  const nothingLeftToPromote = (installed || !canInstall) && pushReady;

  if (dismissed || nothingLeftToPromote) return null;

  return (
    <div className="mx-2 mb-3 rounded-2xl bg-gradient-to-br from-emerald-600 to-cyan-700 p-3 shadow-lg relative" data-testid="doserne-install-banner">
      <button
        onClick={doDismiss}
        className="absolute top-2 right-2 w-6 h-6 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"
        style={{ color: "#fff" }}
        title="Senere"
        data-testid="install-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="text-[10px] uppercase tracking-widest opacity-90 font-black" style={{ color: "#fff" }}>Kom i gang</div>
      <div className="text-sm font-black leading-tight mt-0.5" style={{ color: "#fff" }}>Sett opp Doserne Academy</div>
      <div className="text-[11px] opacity-90 mt-1" style={{ color: "#fff" }}>Installer appen og få varsler i det nye chat og planer kommer.</div>

      <div className="mt-3 space-y-2">
        {canInstall && !installed && (
          <button
            onClick={doInstall}
            disabled={busy}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-emerald-700 text-xs font-black disabled:opacity-60"
            data-testid="install-app-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Installer Doserne-appen
          </button>
        )}
        {installed && (
          <div className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 text-[11px] font-black" style={{ color: "#fff" }}>
            <Check className="w-3.5 h-3.5" /> Appen er installert
          </div>
        )}

        {push.supported && !push.subscribed && push.permission !== "denied" && (
          <button
            onClick={doEnablePush}
            disabled={busy || push.busy}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-black disabled:opacity-60"
            style={{ color: "#fff" }}
            data-testid="install-enable-push-btn"
          >
            {busy || push.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Aktiver varsler
          </button>
        )}
        {push.subscribed && (
          <div className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 text-[11px] font-black" style={{ color: "#fff" }}>
            <Check className="w-3.5 h-3.5" /> Varsler er på
          </div>
        )}
        {push.permission === "denied" && (
          <div className="w-full text-[10px] opacity-80" style={{ color: "#fff" }}>
            Du har blokkert varsler. Slå dem på fra nettleserens innstillinger.
          </div>
        )}
      </div>
    </div>
  );
}
